import { Injectable } from '@angular/core';
import { environment } from 'projects/evolutics-shared-lib/src/environments/environment';
import { map, switchMap } from 'rxjs/operators';
import { IGetQuery, ISearchResponse2, ITableName, InputType } from '@Shared/models/index.model';
import { ApiService } from './api.service';
import { IFieldNameKVP, ReportFieldType } from '@Reusables/reusable-pages/Report/report-extras/report.model';
import { uniqBy } from 'lodash-es';
import { UtilityService } from './utility.service';
import { ITableRelation } from '../Shared/models/table.model';
import { CodeService } from '@Client/app/Services/code.service';

@Injectable({
  providedIn: 'root',
})
export class TableRelationsService {
  readonly baseURL = environment.useGatewayMS
    ? environment.gatewayBaseURL + '/reports/tables/relation/'
    : environment.apiBaseUrl + '/rest/tables/relation/';
  private baseUrl2 = environment.useGatewayMS
    ? environment.gatewayBaseURL + '/reports/relation'
    : environment.gatewayBaseURL + '';
  private reportUrl = environment.useGatewayMS
    ? environment.gatewayBaseURL + '/reports'
    : environment.gatewayBaseURL + '';
  readonly reportFieldTypeToInputTypeMap: { [k in ReportFieldType]: InputType } = {
    BOOLEAN: 'checkbox',
    TEXT: 'text',
    DATE: 'date',
    NUMBER: 'number',
  };
  constructor(
    private apiService: ApiService,
    public uS: UtilityService,
    private codeS: CodeService,
  ) {}

  getAllTableRelations = () => {
    return this.apiService
      .getWithLocalCache<ISearchResponse2<ITableRelation>>(`${this.baseURL}search`)
      .pipe(map((r) => r.content));
  };
  getAllTableGroups = () => {
    return this.apiService.getWithLocalCache<string[]>(`${this.baseUrl2}/table-group`);
  };

  getTableGroups = () => {
    return this.codeS.getCodesByCodeSubGroup('TABLE_GROUP');
  };

  getAllTableFields = () => {
    return this.apiService.getWithLocalCache<ISearchResponse2<ITableRelation>>(`${this.baseURL}search`).pipe(
      map((r) => {
        const relations = uniqBy(r.content, 'primaryKey');
        const fieldNames = relations.map((x) => x.primaryKey);
        return fieldNames;
      }),
    );
  };
  getTablesByTableGroup = (tableGroup: string) => {
    return this.apiService
      .getWithLocalCache<ISearchResponse2<ITableName>>(`${this.baseURL}search?tableGroup=${tableGroup}`)
      .pipe(
        map((r) =>
          r.content
            .sort3('primaryTable')
            .map<ITableName>((x) => ({ _label: x.primaryTable?.split('_').join(' '), ...x })),
        ),
      );
  };
  getTablesByTableGroup2 = (tableGroup?: string) => {
    return this.apiService
      .getWithLocalCache<
        ISearchResponse2<ITableName>
      >(`${this.baseURL}search`, { tableGroup, pageSize: 99999 })
      .pipe(map((r) => r.content));
  };
  getFieldNames = (tableName: string, schema?: string) => {
    return this.apiService
      .getWithLocalCache<IFieldNameKVP[]>(this.reportUrl + `/${tableName}/all-column`)
      .pipe(
        map((x) =>
          x.sort2('columnName', true).map<IFieldNameKVP>((r) => ({
            ...r,
            _inputType: this.reportFieldTypeToInputTypeMap[r.dataType] || 'text',
            _formattedName: this.uS.fieldToLabelMap(r?.columnName?.toLowerCase().replaceAll('_', ' ')),
          })),
        ),
      );
  };
  getFieldNamesAndSchema = (primaryTable: string) => {
    return this.searchTables({ primaryTable }).pipe(
      switchMap((res) => this.getFieldNames(primaryTable, res.content[0]?.schema)),
    );
  };
  getAllTables = () => {
    return this.apiService
      .getFromMemory<
        ISearchResponse2<any>
      >(`${this.reportUrl}/tables/relation/search`, { page: 1, pageSize: 99999 })
      .pipe(map((res) => res.content.map((x) => x.primaryTable as string)));
  };
  searchTables = (query: IGetQuery<{ primaryTable: string; schema: string; tableGroup: string }>) => {
    return this.apiService.get<ISearchResponse2<ITableRelation>>(`${this.baseURL}search`, query);
  };
  searchTablesByName = (primaryTable: string) => {
    return this.searchTables({ primaryTable }).pipe(map((r) => uniqBy(r.content, 'primaryTable')));
  };
  updateTable = (query: ITableRelation, id: number) => {
    return this.apiService.put<ITableRelation>(`${this.baseURL}${id}`, query);
  };
}
